import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarCheck,
  CreditCard,
  Loader2,
  MessageSquare,
  Shield,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useOrg } from '@/contexts/OrgContext';
import { ViewSessionDialog } from '@/components/platform/ViewSessionDialog';

const DEMO_ORG_ID = 'a90138ee-26a5-4570-97b0-5a1144b9ad85';

type Overview = {
  organizations: {
    total: number;
    created_30_days: number;
    active: number;
    trials_active: number;
    trials_ending_7_days: number;
  };
  customers: {
    total: number;
    created_month: number;
    recent: Array<{ id: string; name: string; email: string; organization_name: string; created_at: string }>;
  };
  bookings: {
    total: number;
    new_requests: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    upcoming: number;
  };
  enquiries: {
    total: number;
    new: number;
    open: number;
    converted: number;
  };
  messages: {
    threads: number;
    booking_threads: number;
    enquiry_threads: number;
    recent_messages: number;
    unread_customer_messages: number;
  };
  financials: {
    total_booking_value: number | null;
    revenue_month: number | null;
    pending_payments: number | null;
    completed_payments: number | null;
    average_booking_value: number | null;
    available: boolean;
  };
  team: {
    total_team_members: number;
  };
};

type Tenant = {
  id: string;
  name: string;
  slug: string;
  subscription_status: string | null;
  owner_email: string | null;
  member_count: number;
  jobs_last_30d: number;
};

const statGroups = [
  {
    title: 'Customers',
    icon: Users,
    stats: [
      ['Total customers', 'customers.total'],
      ['New this month', 'customers.created_month'],
    ],
  },
  {
    title: 'Bookings',
    icon: CalendarCheck,
    stats: [
      ['Total bookings', 'bookings.total'],
      ['New requests', 'bookings.new_requests'],
      ['Confirmed', 'bookings.confirmed'],
      ['Completed', 'bookings.completed'],
      ['Cancelled', 'bookings.cancelled'],
      ['Upcoming', 'bookings.upcoming'],
    ],
  },
  {
    title: 'Enquiries',
    icon: MessageSquare,
    stats: [
      ['Total enquiries', 'enquiries.total'],
      ['New this week', 'enquiries.new'],
      ['Open', 'enquiries.open'],
      ['Converted', 'enquiries.converted'],
    ],
  },
  {
    title: 'Messages',
    icon: MessageSquare,
    stats: [
      ['Open threads', 'messages.threads'],
      ['Unread customer messages', 'messages.unread_customer_messages'],
      ['Recent messages', 'messages.recent_messages'],
      ['Booking threads', 'messages.booking_threads'],
      ['Enquiry threads', 'messages.enquiry_threads'],
    ],
  },
  {
    title: 'Organisations',
    icon: Building2,
    stats: [
      ['Total organisations', 'organizations.total'],
      ['Active organisations', 'organizations.active'],
      ['Created in 30 days', 'organizations.created_30_days'],
      ['Active trials', 'organizations.trials_active'],
      ['Trials ending soon', 'organizations.trials_ending_7_days'],
    ],
  },
  {
    title: 'Financials',
    icon: CreditCard,
    stats: [
      ['Total booking value', 'financials.total_booking_value'],
      ['Revenue this month', 'financials.revenue_month'],
      ['Pending payments', 'financials.pending_payments'],
      ['Completed payments', 'financials.completed_payments'],
      ['Average booking value', 'financials.average_booking_value'],
    ],
  },
];

function getValue(data: Overview | undefined, path: string): number | null {
  if (!data) return 0;
  return (path.split('.').reduce<unknown>((current, key) => (current as Record<string, unknown> | null | undefined)?.[key], data) ?? null) as number | null;
}

const PlatformOverview = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useOrg();

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-overview'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_platform_overview' as never);
      if (error) throw error;
      return data as Overview;
    },
  });

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['platform-overview-tenants'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_tenants' as never, {
        _search: null,
        _status: null,
        _limit: 8,
        _offset: 0,
      } as never);
      if (error) throw error;
      return (data || []) as Tenant[];
    },
  });

  const openTenantAdmin = async (orgId: string, reason: string, caseRef?: string) => {
    await startImpersonation(orgId, reason, caseRef);
    navigate('/admin');
  };

  const demoTenant = tenants.find((tenant) => tenant.id === DEMO_ORG_ID || tenant.slug === 'the-polished-version');

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="font-display text-3xl text-foreground">Platform Admin Dashboard</h2>
          <p className="mt-1 text-muted-foreground">
            Control centre for The Polished Version across organisations, customers, bookings and messages.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline"><Link to="/platform/tenants">View organisations</Link></Button>
          {demoTenant && (
            <ViewSessionDialog
              tenantName={demoTenant.name}
              onConfirm={(reason, caseRef) => openTenantAdmin(demoTenant.id, reason, caseRef)}
              trigger={
                <Button>
                  <Shield className="mr-2 h-4 w-4" />
                  Demo admin access
                </Button>
              }
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-6 text-destructive">Unable to load platform metrics.</CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {statGroups.map((group) => {
            const Icon = group.icon;
            const isFinancial = group.title === 'Financials';
            return (
              <Card key={group.title}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Icon className="h-5 w-5 text-primary" />
                      {group.title}
                    </CardTitle>
                    {isFinancial && !data?.financials?.available && (
                      <Badge variant="outline">Not live yet</Badge>
                    )}
                  </div>
                  {isFinancial && (
                    <CardDescription>
                      Payment/revenue fields are reserved for the payments layer.
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3">
                  {group.stats.map(([label, path]) => {
                    const value = getValue(data, path);
                    return (
                      <div key={path} className="rounded-md border bg-muted/20 p-3">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="mt-1 text-2xl font-semibold text-foreground">
                          {value === null ? '-' : value}
                        </p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <Card>
          <CardHeader>
            <CardTitle>Organisation access</CardTitle>
            <CardDescription>Open an organisation admin area without making platform data visible to normal org admins.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {tenantsLoading ? (
              <div className="flex h-24 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : tenants.length === 0 ? (
              <p className="text-sm text-muted-foreground">No organisations found.</p>
            ) : (
              tenants.map((tenant) => (
                <div key={tenant.id} className="flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{tenant.name}</p>
                      {tenant.id === DEMO_ORG_ID && <Badge variant="secondary">Demo</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {tenant.slug}.thepolishedversion.com · {tenant.subscription_status || 'unknown'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Owner: {tenant.owner_email || '-'} · Members: {tenant.member_count} · Jobs 30d: {tenant.jobs_last_30d}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" asChild>
                      <Link to={`/platform/tenants/${tenant.id}`}>Details</Link>
                    </Button>
                    <ViewSessionDialog
                      tenantName={tenant.name}
                      onConfirm={(reason, caseRef) => openTenantAdmin(tenant.id, reason, caseRef)}
                      trigger={<Button>Open admin</Button>}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent customer signups</CardTitle>
            <CardDescription>Latest customer records across organisations.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data?.customers?.recent || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No customer records yet.</p>
            ) : (
              data?.customers?.recent?.map((customer) => (
                <div key={customer.id} className="rounded-md border p-3">
                  <p className="font-medium">{customer.name}</p>
                  <p className="text-sm text-muted-foreground">{customer.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {customer.organization_name} · {new Date(customer.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlatformOverview;
